import {
  createSelector,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit";
import { EVENT_CAPABILITIES } from "../../constants/permissions";
import type { CollinsUser } from "../../types/user";
import {
  FLAGS_WITHOUT_EARLY_ACCESS,
  getDefaultFeatureFlags
} from "../../utils/localStorage/featureFlagsStorage";
import type { RootState } from "../store";

export type FeatureFlag = {
  key: string;
  title: string;
  description?: string;
  isEnabled: boolean;
};

export type FeatureFlagsState = {
  flags: FeatureFlag[];
  storedFlagsLoaded: boolean;
};

export const initialFeatureFlagsState: FeatureFlagsState = {
  flags: [],
  storedFlagsLoaded: false
};

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState: initialFeatureFlagsState,
  reducers: {
    featureFlagsLoadRequested: () => {},
    storedFeatureFlagsLoaded: (state, action: PayloadAction<FeatureFlag[]>) => {
      state.flags = action.payload;
      state.storedFlagsLoaded = true;
    },
    featureFlagsChanged: (state, action: PayloadAction<FeatureFlag[]>) => {
      state.flags = action.payload;
    },
    featureFlagToggled: (
      state,
      action: PayloadAction<{ key: string; isEnabled: boolean }>
    ) => {
      state.flags = state.flags.map((flag) =>
        flag.key === action.payload.key
          ? { ...flag, isEnabled: action.payload.isEnabled }
          : flag
      );
    }
  }
});

export const {
  featureFlagsLoadRequested,
  storedFeatureFlagsLoaded,
  featureFlagsChanged,
  featureFlagToggled
} = featureFlagsSlice.actions;

export const selectStoredFeatureFlags = createSelector(
  [(state: RootState) => state.featureFlags],
  (featureFlags) => featureFlags.flags
);

const selectCollinsUser = (state: RootState): CollinsUser | undefined =>
  state.session.collinsUser;

export const selectHasEarlyAccess = createSelector(
  [selectCollinsUser],
  (user) =>
    !!user?.capabilities?.includes(EVENT_CAPABILITIES.HAS_EARLY_ACCESS_PREVIEW)
);

export const selectEffectiveFeatureFlags = createSelector(
  [selectStoredFeatureFlags, selectHasEarlyAccess],
  (flags, hasEarlyAccess) => {
    if (hasEarlyAccess) {
      return flags;
    }

    const defaults = getDefaultFeatureFlags();
    return flags.map((flag) =>
      FLAGS_WITHOUT_EARLY_ACCESS.includes(flag.key)
        ? flag
        : {
            ...flag,
            isEnabled:
              defaults.find((defaultFlag) => defaultFlag.key === flag.key)
                ?.isEnabled ?? false
          }
    );
  }
);

export const selectFeatureFlagsLoaded = createSelector(
  [
    (state: RootState) => state.featureFlags.storedFlagsLoaded,
    selectCollinsUser
  ],
  (storedFlagsLoaded, user) => storedFlagsLoaded && user !== undefined
);

export const featureFlagsReducer = featureFlagsSlice.reducer;
export default featureFlagsReducer;
